import { useState } from "react";
import { X, LoaderCircle } from "lucide-react";
import { toast } from "react-toastify";
import { api, apiUrls } from "../utils/api";
import { trackEvent } from "../utils/trackEvent";

interface Plan {
  id: string;
  credits: number;
  price: number;
  badge?: string;
}

const PLANS: Plan[] = [
  { id: "ot-10", credits: 10, price: 3.99 },
  { id: "ot-20", credits: 20, price: 5.99, badge: "Popular" },
  { id: "ot-50", credits: 50, price: 9.99, badge: "Best Value" },
];

interface PricingModalProps {
  onClose: () => void;
  artStyle?: string;
}

export function PricingModal({ onClose, artStyle }: PricingModalProps) {
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);

  const handleBuy = async (plan: Plan) => {
    setLoadingPlanId(plan.id);
    trackEvent("Buying credits", {
      planId: plan.id,
      price: plan.price,
      currency: "USD",
    });

    try {
      const res = await api.post(apiUrls.createCheckoutSession, {
        planId: plan.id,
        artStyle: artStyle ?? "ghibli",
        cancelUrl: window.location.origin + window.location.pathname + window.location.search,
        successUrl: window.location.origin + window.location.pathname + window.location.search,
      });

      const { url } = res.data;
      if (url) {
        window.location.href = url;
      } else {
        toast.error("Could not start checkout. Please try again.");
        setLoadingPlanId(null);
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Error during checkout";
      toast.error(message);
      setLoadingPlanId(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-headline text-xl font-bold text-slate-900">
            Get Your Full-Quality Artwork
          </h2>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="mb-5 text-sm text-slate-500">
          Download clean, print-ready versions of your artwork — no watermarks, no compromise.
        </p>

        {/* Plan cards */}
        <div className="space-y-3">
          {PLANS.map((plan) => {
            const isPopular = plan.badge === "Popular";
            const isBest = plan.badge === "Best Value";
            const isLoading = loadingPlanId === plan.id;
            const isDisabled = loadingPlanId !== null;

            return (
              <div
                key={plan.id}
                className={`relative rounded-xl border-2 p-4 transition-shadow ${
                  isPopular
                    ? "border-primary-400 shadow-md"
                    : isBest
                      ? "border-amber-500 shadow-md"
                      : "border-slate-200"
                }`}
              >
                {plan.badge && (
                  <span
                    className={`absolute -top-2.5 right-3 rounded-full px-2.5 py-0.5 text-[11px] font-bold ${
                      isPopular
                        ? "bg-primary-400 text-white"
                        : "bg-amber-500 text-white"
                    }`}
                  >
                    {plan.badge}
                  </span>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-base font-semibold text-slate-900">
                      {plan.credits} creations
                    </p>
                  </div>
                  <p className="text-lg font-bold text-slate-900">
                    ${plan.price.toFixed(2)}
                  </p>
                </div>

                <button
                  onClick={() => handleBuy(plan)}
                  disabled={isDisabled}
                  className={`mt-3 flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
                    isPopular
                      ? "bg-primary-400 text-white hover:bg-primary-500 disabled:opacity-60"
                      : isBest
                        ? "bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-60"
                        : "bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-60"
                  }`}
                >
                  {isLoading ? (
                    <>
                      <LoaderCircle className="h-4 w-4 animate-spin" />
                      Redirecting...
                    </>
                  ) : (
                    `Get ${plan.credits} Creations — $${plan.price.toFixed(2)}`
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <p className="mt-4 text-center text-xs text-slate-400">
          Secure checkout via Stripe
        </p>
      </div>
    </div>
  );
}
